import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Check, Menu, X, Mail, Phone, MessageCircle } from "lucide-react";
import { CATALOGUE, TRADITIONS, TRUST_ITEMS, HOW_STEPS, LANG_CHIPS, IMAGES } from "./landing/data";
import { BLOG_POSTS, formatDate } from "@/data/blog";
import { VidhanaSoudhaIcon } from "./landing/icons";
import { WaitlistModal } from "./landing/WaitlistModal";
import { PanchangaWidget } from "./landing/PanchangaWidget";
import { openBooking, bookingUrl, trackBooking } from "@/lib/booking";

/* ---------- shared ---------- */
const Reveal = ({ children, delay = 0, className }) => {
  const reduce = useReducedMotion();
  return (
    <motion.div
      className={className}
      initial={reduce ? false : { opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.55, delay, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
};

const SectionHead = ({ eyebrow, title, intro, id }) => (
  <Reveal className="mx-auto max-w-3xl text-center">
    {eyebrow && <p className="font-baloo text-sm tracking-wide text-[#A8722A]">{eyebrow}</p>}
    <h2 id={id} className="mt-1 font-eczar text-3xl font-semibold text-[#3B221B] md:text-4xl">{title}</h2>
    {intro && <p className="mt-3 font-mukta text-base text-[#6B5044] md:text-lg">{intro}</p>}
  </Reveal>
);

const PrimaryBtn = ({ children, onClick, testId, className = "" }) => (
  <button onClick={onClick} data-testid={testId}
    className={`rounded-full bg-[#B23A2B] px-6 py-3 font-mukta font-semibold text-[#FFFDF8] shadow-sm transition-colors hover:bg-[#9a2f22] focus:outline-none focus:ring-2 focus:ring-[#B23A2B]/50 focus:ring-offset-2 ${className}`}>
    {children}
  </button>
);

const OutlineBtn = ({ children, onClick, testId, className = "" }) => (
  <button onClick={onClick} data-testid={testId}
    className={`rounded-full border border-[#A8722A] px-6 py-3 font-mukta font-semibold text-[#A8722A] transition-colors hover:bg-[#A8722A]/10 focus:outline-none focus:ring-2 focus:ring-[#A8722A]/50 focus:ring-offset-2 ${className}`}>
    {children}
  </button>
);

/* ---------- 1. header ---------- */
const NAV = [
  { label: "Occasions", to: "/occasions" },
  { label: "Panchanga", to: "/panchanga" },
  { label: "Astrology", to: "/astrology" },
  { label: "For Patashala Students", to: "/patashala" },
  { label: "Stotras", to: "/stotras" },
  { label: "Heritage", to: "/heritage" },
  { label: "Festival Calendar", to: "/festivals" },
  { label: "Blogs", to: "/blog" },
];

const Header = ({ kn, setKn, openWaitlist }) => {
  const [menu, setMenu] = useState(false);
  return (
    <header className="sticky top-0 z-50 border-b border-[#A8722A]/20 bg-[#FBF6EC]/95 backdrop-blur-md">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 md:px-8">
        <a href="#top" className="flex items-center" data-testid="header-logo" aria-label="PurohithConnect™ home">
          <img src="/brand/pc-wordmark.png" alt="PurohithConnect™" className="h-7 w-auto sm:h-8" />
        </a>
        <nav className="hidden items-center gap-6 lg:flex" aria-label="Main navigation">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/\s|&/g, "-")}`}
              className="font-mukta text-sm font-medium text-[#6B5044] transition-colors hover:text-[#3B221B] focus:outline-none focus:ring-2 focus:ring-[#A8722A]/50 rounded">
              {n.label}
            </Link>
          ))}
        </nav>
        <div className="flex items-center gap-2.5">
          <button onClick={() => setKn(!kn)} aria-pressed={kn} data-testid="lang-toggle-chip"
            className={`rounded-full border px-3.5 py-1.5 font-baloo text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-[#A8722A]/50 ${kn ? "border-[#B23A2B] bg-[#B23A2B] text-[#FFFDF8]" : "border-[#A8722A]/40 bg-[#FFFDF8] text-[#A8722A]"}`}>
            ಕನ್ನಡ
          </button>
          <PrimaryBtn onClick={() => openWaitlist("Family")} testId="header-early-access-btn" className="hidden px-5 py-2 text-sm sm:block">
            Book now
          </PrimaryBtn>
          <button className="lg:hidden rounded p-1.5 text-[#3B221B] focus:outline-none focus:ring-2 focus:ring-[#A8722A]/50" onClick={() => setMenu(!menu)}
            aria-label={menu ? "Close menu" : "Open menu"} data-testid="mobile-menu-btn">
            {menu ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      {menu && (
        <nav className="border-t border-[#A8722A]/15 bg-[#FBF6EC] px-6 py-4 lg:hidden" aria-label="Mobile navigation" data-testid="mobile-menu">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} onClick={() => setMenu(false)} className="block py-2 font-mukta text-[#3B221B]">{n.label}</Link>
          ))}
          <PrimaryBtn onClick={() => openWaitlist("Family")} testId="mobile-early-access-btn" className="mt-2 w-full text-sm">Book now</PrimaryBtn>
        </nav>
      )}
    </header>
  );
};

/* ---------- 2. hero ---------- */
const CITY_SIGNUPS = [
  { city: "Whitefield", count: "180+", pct: 100 },
  { city: "Jayanagar", count: "150+", pct: 83 },
  { city: "Malleshwaram", count: "120+", pct: 67 },
  { city: "HSR Layout", count: "110+", pct: 61 },
  { city: "Yelahanka", count: "90+", pct: 50 },
];

const CityRow = ({ c, i }) => {
  const reduce = useReducedMotion();
  return (
    <div className="py-1.5" data-testid={`teaser-city-${c.city.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-baseline justify-between gap-3">
        <span className="font-mukta text-sm text-[#3B221B]">{c.city}</span>
        <span className="font-eczar text-sm font-semibold text-[#A8722A]">{c.count} families</span>
      </div>
      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-[#F3EADA]" aria-hidden="true">
        <motion.div
          className="h-full rounded-full bg-gradient-to-r from-[#C99B55] to-[#A8722A]"
          initial={reduce ? { width: `${c.pct}%` } : { width: 0 }}
          whileInView={{ width: `${c.pct}%` }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, delay: 0.2 + i * 0.12, ease: "easeOut" }}
        />
      </div>
    </div>
  );
};

const LaunchTeaserCard = ({ openWaitlist }) => (
  <div className="rounded-[14px] border-4 border-double border-[#A8722A]/60 bg-[#FFFDF8] p-6 shadow-xl md:p-7" data-testid="hero-launch-teaser-card">
    <div className="flex items-center justify-center gap-2">
      <span className="pc-live-dot h-2 w-2 rounded-full bg-[#B23A2B]" aria-hidden="true" />
      <p className="font-mukta text-xs font-semibold uppercase tracking-widest text-[#B23A2B]">Launching soon in Bengaluru</p>
    </div>
    <h3 className="mt-2 text-center font-eczar text-xl font-bold text-[#3B221B] md:text-2xl">Early access is filling up</h3>
    <p className="mt-1 text-center font-baloo text-sm text-[#A8722A]">ಶೀಘ್ರದಲ್ಲೇ ನಿಮ್ಮ ಬೆಂಗಳೂರಿನಲ್ಲಿ</p>
    <div className="mt-4 border-t border-dashed border-[#A8722A]/40 pt-3">
      {CITY_SIGNUPS.map((c, i) => <CityRow key={c.city} c={c} i={i} />)}
    </div>
    <div className="mt-4 flex items-center justify-between gap-3 border-t border-dashed border-[#A8722A]/40 pt-4">
      <p className="font-mukta text-xs text-[#6B5044]" data-testid="teaser-total-count">
        <span className="font-eczar text-base font-bold text-[#3E6B4F]">650+ families</span> & <span className="font-eczar text-base font-bold text-[#3E6B4F]">60+ purohiths</span><br />already on the list
      </p>
      <button onClick={() => openWaitlist("Family")} data-testid="teaser-early-access-btn"        className="shrink-0 rounded-full bg-[#B23A2B] px-4 py-2 font-mukta text-sm font-semibold text-[#FFFDF8] transition-colors hover:bg-[#9a2f22] focus:outline-none focus:ring-2 focus:ring-[#B23A2B]/50">
        Book now
      </button>
    </div>
  </div>
);

const Hero = ({ kn, openWaitlist }) => {
  const [lang, setLang] = useState(LANG_CHIPS[0]);
  return (
    <section id="top" className="relative overflow-hidden">
      <div className="relative z-10 mx-auto grid max-w-7xl gap-12 px-4 pb-16 pt-10 md:grid-cols-2 md:items-center md:px-8 md:pt-16 lg:gap-16">
      <Reveal>
        <p className="font-mukta text-sm font-semibold uppercase tracking-widest text-[#A8722A]" data-testid="hero-eyebrow">
          Made in Bengaluru · Launching soon
        </p>
        <p className="mt-3 inline-flex items-center gap-2 rounded-full border border-[#A8722A]/30 bg-[#FFFDF8]/80 px-4 py-1.5 font-baloo text-sm text-[#A8722A] backdrop-blur-sm" data-testid="hero-blessing-ribbon">
          <span className="text-[#B23A2B]">॥</span>
          {kn
            ? "ಕರ್ನಾಟಕದ ಪವಿತ್ರ ಪರಂಪರೆಗಳ ಪ್ರೇರಣೆಯಿಂದ"
            : "Inspired by Karnataka's sacred peethams"}
          <span className="text-[#B23A2B]">॥</span>
        </p>
        <h1 className="mt-3 font-eczar text-4xl font-bold leading-tight text-[#3B221B] sm:text-5xl lg:text-6xl" data-testid="hero-heading">
          {kn ? (
            <>ಪ್ರತಿ <span className="text-[#B23A2B]">ಶುಭ ಕಾರ್ಯಕ್ಕೂ</span> ಸರಿಯಾದ ಪುರೋಹಿತರು.</>
          ) : (
            <>The right purohith for every <span className="text-[#B23A2B]">sacred occasion</span>.</>
          )}
        </h1>
        <p className="mt-4 max-w-xl font-mukta text-base text-[#6B5044] md:text-lg">
          {kn
            ? "ಗೃಹ ಪ್ರವೇಶದಿಂದ ನಾಮಕರಣದವರೆಗೆ — ನಿಮ್ಮ ಭಾಷೆ ಮತ್ತು ಸಂಪ್ರದಾಯದಲ್ಲಿ, ನಿಮ್ಮ ಕುಟುಂಬಕ್ಕೆ ಪರಿಚಿತವಾದ ರೀತಿಯಲ್ಲಿ ವಿಧಿ ನಡೆಸುವ KYC-ಪರಿಶೀಲಿತ ಪುರೋಹಿತರು."
            : "From griha pravesha to naamakarana — a KYC-verified purohith who performs your ceremony in your language and your tradition, the way your family has always known it."}
        </p>
        <div className="mt-6 flex flex-wrap gap-2" role="radiogroup" aria-label="Language preference">
          {LANG_CHIPS.map((l) => (
            <button key={l} role="radio" aria-checked={lang === l} onClick={() => setLang(l)}
              data-testid={`hero-lang-chip-${l.split(" ")[0].toLowerCase()}`}
              className={`rounded-full border px-4 py-1.5 font-mukta text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-[#A8722A]/50 ${lang === l ? "border-[#B23A2B] bg-[#B23A2B] text-[#FFFDF8]" : "border-[#A8722A]/35 bg-[#FFFDF8] text-[#6B5044] hover:border-[#A8722A]/60"}`}>
              {l}
            </button>
          ))}
        </div>
        <div className="mt-7 flex flex-wrap gap-3">
          <PrimaryBtn onClick={() => openWaitlist("Family")} testId="hero-book-btn">Book a purohith</PrimaryBtn>
          <OutlineBtn onClick={() => openWaitlist("Purohith")} testId="hero-join-priest-btn">Join as a purohith — free</OutlineBtn>
        </div>
        <p className="mt-4 font-mukta text-sm text-[#6B5044]">
          Every purohith verified · All traditions and communities welcome
        </p>
      </Reveal>
      <Reveal delay={0.15}>
        <div className="relative mx-auto max-w-md md:max-w-none">
          <img src={IMAGES.hanuman}
            alt="Lord Hanuman blessing PurohithConnect — your trusted partner for sacred occasions"
            className="w-full rounded-[18px] object-cover shadow-xl ring-1 ring-[#A8722A]/20"
            data-testid="hero-hanuman-image" />
        </div>
      </Reveal>
      </div>
    </section>
  );
};

/* ---------- 2b. blessed traditions ---------- */
const BLESSED = [
  {
    img: IMAGES.sringeri,
    kn: "ಶ್ರೀ ಶಾರದಾ ಪೀಠ, ಶೃಂಗೇರಿ",
    en: "Sri Sharada Peetham, Sringeri",
    descKn: "ಕರ್ನಾಟಕದ ವೇದ ವಿದ್ಯೆಯ ಆದಿ ಮೂಲ",
    descEn: "The fountainhead of Karnataka's Vedic scholarship",
    testId: "sringeri",
  },
  {
    img: IMAGES.dharmasthala,
    kn: "ಶ್ರೀ ಮಂಜುನಾಥ ಸ್ವಾಮಿ, ಧರ್ಮಸ್ಥಳ",
    en: "Sri Manjunatha Swamy, Dharmasthala",
    descKn: "ಧರ್ಮದ ನೆಲೆ, ಕರಾವಳಿಯ ಪವಿತ್ರ ಕ್ಷೇತ್ರ",
    descEn: "The abode of Dharma on the coastal pilgrimage",
    testId: "dharmasthala",
  },
];

const BlessedTraditions = ({ kn }) => (
  <section className="relative overflow-hidden border-y border-[#A8722A]/20 bg-[#F6ECDB]" data-testid="blessed-traditions-section">
    <img
      src={IMAGES.hanuman}
      alt=""
      aria-hidden="true"
      loading="lazy"
      className="pointer-events-none absolute -left-16 bottom-0 z-0 h-[115%] w-auto max-w-none select-none object-contain opacity-[0.06] md:opacity-[0.08]"
    />
    <div className="relative z-10 mx-auto max-w-7xl px-4 py-14 md:px-8 md:py-16">
      <Reveal className="mx-auto max-w-3xl text-center">
        <p className="font-baloo text-base text-[#B23A2B]">
          {kn ? "ಗುರು ಪರಂಪರೆಯ ಆಶೀರ್ವಾದದಲ್ಲಿ" : "In reverence to our guru paramparā"}
        </p>
        <h2 className="mt-1 font-eczar text-3xl font-semibold text-[#3B221B] md:text-4xl">
          {kn ? "ಕರ್ನಾಟಕದ ಪವಿತ್ರ ಪೀಠಗಳ ಪ್ರೇರಣೆಯಿಂದ" : "Inspired by Karnataka's sacred peethams"}
        </h2>
        <p className="mt-3 font-mukta text-base text-[#6B5044] md:text-lg">
          {kn
            ? "ನಮ್ಮ ಪುರೋಹಿತರು ಈ ಪರಂಪರೆಗಳ ವಿಧಿ ಮತ್ತು ಪದ್ಧತಿಯಲ್ಲಿ ಪರಿಣಿತರು — ಪ್ರತಿ ಕಾರ್ಯವೂ ಶ್ರದ್ಧೆಯಿಂದ, ಸಂಪ್ರದಾಯದಂತೆ."
            : "Our purohiths are trained in the vidhi and paddhati of these revered traditions — every ceremony performed with devotion, the way it has always been."}
        </p>
      </Reveal>
      <div className="mt-10 grid gap-6 sm:grid-cols-2">
        {BLESSED.map((b, i) => (
          <Reveal key={b.testId} delay={i * 0.1}>
            <article
              className="group h-full overflow-hidden rounded-[18px] border border-[#A8722A]/25 bg-[#FFFDF8] shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
              data-testid={`blessed-card-${b.testId}`}
            >
              <div className="h-56 overflow-hidden bg-[#F3EADA]/60 md:h-64">
                <img
                  src={b.img}
                  alt={b.en}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  data-testid={`blessed-img-${b.testId}`}
                />
              </div>
              <div className="p-6">
                <p className="font-baloo text-lg text-[#B23A2B]">{b.kn}</p>
                <h3 className="mt-0.5 font-eczar text-xl font-semibold text-[#3B221B]">{b.en}</h3>
                <p className="mt-2 font-mukta text-sm text-[#6B5044]">{kn ? b.descKn : b.descEn}</p>
              </div>
            </article>
          </Reveal>
        ))}
      </div>
      <p className="mt-6 text-center font-mukta text-xs text-[#6B5044]/80">
        {kn
          ? "ಚಿತ್ರಗಳು ಗೌರವಪೂರ್ವಕ ಪ್ರಾತಿನಿಧಿಕ. PurohithConnect™ ಈ ಸಂಸ್ಥೆಗಳೊಂದಿಗೆ ಅಧಿಕೃತ ಸಂಬಂಧ ಹೊಂದಿಲ್ಲ."
          : "Images are respectful representations. PurohithConnect™ is not officially affiliated with these institutions."}
      </p>
    </div>
  </section>
);

/* ---------- 3. trust strip ---------- */
const TrustStrip = () => (
  <section className="border-y border-[#A8722A]/20 bg-[#F3EADA]">
    <div className="mx-auto grid max-w-7xl grid-cols-1 gap-6 px-4 py-10 sm:grid-cols-2 md:px-8 lg:grid-cols-4">
      {TRUST_ITEMS.map(({ Icon, t, d }, i) => (
        <Reveal key={t} delay={i * 0.08} className="flex items-start gap-4">
          <div className="shrink-0"><Icon size={42} /></div>
          <div>
            <p className="font-eczar text-base font-semibold text-[#3B221B]">{t}</p>
            <p className="font-mukta text-sm text-[#6B5044]">{d}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

/* ---------- 4. catalogue ---------- */
const slugify = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-");

const ServiceCard = ({ s, i, openWaitlist }) => (
  <Reveal delay={(i % 3) * 0.07}>
    <article className="flex h-full flex-col rounded-[14px] border border-[#A8722A]/25 bg-[#FFFDF8] shadow-sm transition-shadow hover:shadow-md" data-testid={`service-card-${slugify(s.en)}`}>
      <a href={bookingUrl(slugify(s.en))} target="_blank" rel="noopener noreferrer" onClick={() => trackBooking({ location: "landing-pooja-card", pooja: slugify(s.en) })} className="block h-48 overflow-hidden rounded-t-[14px] bg-[#F3EADA]/50" aria-label={`Book ${s.en}`}>
        <img src={`/pooja-art/${slugify(s.en)}.jpg`} alt={`${s.en} illustration`} loading="lazy"
          className="h-full w-full object-cover transition-transform duration-500 hover:scale-105" data-testid={`service-img-${slugify(s.en)}`} />
      </a>
      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-start gap-3">
          <s.Icon size={34} />
          <div>
            <h3 className="font-eczar text-lg font-semibold text-[#3B221B]">{s.en}</h3>
            <p className="font-baloo text-base text-[#C99B55]">{s.kn}</p>
          </div>
        </div>
        <ul className="mt-3 flex-1 space-y-1.5">
          {s.bullets.map((b) => (
            <li key={b} className="flex items-start gap-2 font-mukta text-sm text-[#6B5044]">
              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#B23A2B]" aria-hidden="true" />{b}
            </li>
          ))}
        </ul>
        <button onClick={() => openWaitlist("Family")} data-testid={`service-details-${slugify(s.en)}`}
          className="mt-4 inline-flex items-center gap-1 font-mukta text-sm font-semibold text-[#A8722A] hover:text-[#8f5f21] focus:outline-none focus:ring-2 focus:ring-[#A8722A]/50 rounded">
          Book now <ArrowRight size={14} />
        </button>
      </div>
    </article>
  </Reveal>
);

const Catalogue = ({ openWaitlist }) => {
  const tabs = Object.keys(CATALOGUE);
  const [tab, setTab] = useState(tabs[0]);
  return (
    <section id="occasions" className="mx-auto max-w-7xl px-4 py-16 md:px-8 md:py-20">
      <SectionHead
        eyebrow="ಶುಭ ಕಾರ್ಯಗಳು"
        title="Browse by occasion"
        intro="Whatever the auspicious reason — a new home, a new name, a new beginning — there is a purohith here who knows it well."
      />
      <div className="mt-8 flex flex-wrap justify-center gap-2" role="tablist" aria-label="Service categories">
        {tabs.map((t) => (
          <button key={t} role="tab" aria-selected={tab === t} onClick={() => setTab(t)}
            data-testid={`catalogue-tab-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={`rounded-full border px-5 py-2 font-mukta text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-[#B23A2B]/50 ${tab === t ? "border-[#B23A2B] bg-[#B23A2B] text-[#FFFDF8]" : "border-[#A8722A]/35 bg-[#FFFDF8] text-[#6B5044] hover:border-[#A8722A]/60"}`}>
            {t}
          </button>
        ))}
      </div>
      <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" role="tabpanel" aria-label={tab}>
        {CATALOGUE[tab].map((s, i) => <ServiceCard key={s.en} s={s} i={i} openWaitlist={openWaitlist} />)}
      </div>
      <div className="mt-8 text-center">
        <Link to="/occasions" data-testid="view-all-occasions"
          className="inline-flex items-center gap-1.5 font-mukta font-semibold text-[#B23A2B] hover:text-[#9a2f22]">
          View all occasions <ArrowRight size={16} />
        </Link>
      </div>
      <div className="mt-10 grid gap-4 md:grid-cols-2">
        <Reveal>
          <div className="h-full rounded-[14px] border border-[#3E6B4F]/30 p-5 font-mukta text-sm text-[#3E6B4F]" style={{ backgroundColor: "rgba(62,107,79,0.07)" }} data-testid="pricing-note-card">
            You'll see the full price on your booking summary before you confirm — no hidden charges, no surprises on the day.
          </div>
        </Reveal>
        <Reveal delay={0.08}>
          <div className="h-full rounded-[14px] border border-[#A8722A]/35 bg-[#F3EADA] p-5 font-mukta text-sm text-[#6B5044]" data-testid="samagri-note-card">
            <span className="font-semibold text-[#A8722A]">Samagri, your way</span> — ask your purohith to bring everything, or receive the complete list in advance and arrange it yourself.
          </div>
        </Reveal>
      </div>
    </section>
  );
};

/* ---------- 5. traditions ---------- */
const Traditions = () => (
  <section id="traditions" className="mx-auto max-w-7xl px-4 py-16 md:px-8 md:py-20">
    <SectionHead
      eyebrow="ಸಂಪ್ರದಾಯ"
      title="Every tradition, honoured"
      intro="Rituals are not one-size-fits-all. Tell us how your family performs them, and we match a purohith or poojari who follows the same path — Brahmin and non-Brahmin traditions alike."
    />
    <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {TRADITIONS.map((t, i) => (
        <Reveal key={t.name} delay={(i % 3) * 0.07}>
          <article className="flex h-full items-start gap-4 rounded-[14px] border border-[#A8722A]/25 bg-[#FFFDF8] p-5 shadow-sm" data-testid={`tradition-card-${t.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <div className="shrink-0"><t.Icon size={44} /></div>
            <div>
              <h3 className="font-eczar text-base font-semibold text-[#3B221B]">{t.name}</h3>
              <p className="font-baloo text-sm text-[#C99B55]">{t.kn}</p>
              <p className="mt-1.5 font-mukta text-sm text-[#6B5044]">{t.desc}</p>
            </div>
          </article>
        </Reveal>
      ))}
    </div>
    <Reveal className="mt-8">
      <div className="rounded-[14px] border border-[#A8722A]/30 bg-[#F3EADA] px-6 py-5 text-center font-mukta text-sm text-[#6B5044] md:text-base" data-testid="traditions-banner">
        Don't see your community's custom listed? Tell us when you book — we onboard purohiths and poojaris across communities, and we'll find the right match for your family.
      </div>
    </Reveal>
    <div className="mt-6 text-center">
      <Link to="/traditions" data-testid="view-all-traditions"
        className="inline-flex items-center gap-1.5 font-mukta font-semibold text-[#B23A2B] hover:text-[#9a2f22]">
        Explore all traditions <ArrowRight size={16} />
      </Link>
    </div>
  </section>
);

/* ---------- 6. Made in Bengaluru ---------- */
const BengaluruBand = () => (
  <section className="mx-auto max-w-4xl px-4 py-16 text-center md:py-20">
    <Reveal>
      <VidhanaSoudhaIcon className="mx-auto w-52 md:w-64" />
      <div className="mt-4 flex justify-center gap-2" aria-hidden="true">
        <span className="h-2.5 w-2.5 rounded-full bg-[#E68A2E]" />
        <span className="h-2.5 w-2.5 rounded-full border border-[#A8722A]/40 bg-white" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#3E6B4F]" />
      </div>
      <h2 className="mt-5 font-eczar text-3xl font-semibold text-[#3B221B] md:text-4xl">
        Built in Bengaluru. Rooted in Karnataka. Made for India.
      </h2>
      <p className="mt-2 font-baloo text-base text-[#A8722A] md:text-lg">
        ಬೆಂಗಳೂರಿನಲ್ಲಿ ನಿರ್ಮಿತ · ಕರ್ನಾಟಕದ ಬೇರು · ಭಾರತಕ್ಕಾಗಿ
      </p>
      <p className="mx-auto mt-4 max-w-2xl font-mukta text-base text-[#6B5044]">
        A homegrown platform — designed, built and supported from Bengaluru, carrying Karnataka's purohith heritage to families across India and beyond.
      </p>
      <div className="mt-6 flex flex-wrap justify-center gap-2.5">
        {["Bengaluru-built product & team", "Kannada-first support", "Serving India & NRI families"].map((c) => (
          <span key={c} className="rounded-full border border-[#A8722A]/35 bg-[#FFFDF8] px-4 py-1.5 font-mukta text-sm text-[#6B5044]">{c}</span>
        ))}
      </div>
    </Reveal>
  </section>
);

/* ---------- 8. dual b2c ---------- */
const DualB2C = ({ openWaitlist }) => (
  <section id="purohiths" className="mx-auto max-w-7xl px-4 py-16 md:px-8 md:py-20">
    <Reveal>
      <div className="rounded-[20px] bg-[#3B221B] p-8 md:p-12">
        <h2 className="text-center font-eczar text-3xl font-semibold text-[#FBF6EC] md:text-4xl">One platform. Two blessings.</h2>
        <p className="mx-auto mt-3 max-w-2xl text-center font-mukta text-base text-[#FBF6EC]/75">
          PurohithConnect works only when both sides win — the family that books, and the purohith who performs.
        </p>
        <div className="mt-10 grid gap-10 md:grid-cols-2 md:gap-8">
          <div className="border-[#C99B55]/25 md:border-r md:pr-8">
            <p className="font-mukta text-xs font-semibold uppercase tracking-widest text-[#C99B55]">For Families</p>
            <h3 className="mt-1 font-eczar text-2xl font-semibold text-[#FBF6EC]">Book with confidence</h3>
            <ul className="mt-4 space-y-2.5">
              {[
                "Identity-verified purohiths",
                "Choose by language, tradition and occasion",
                "Samagri brought or listed in advance",
                "Confirmed muhurtha, confirmed arrival",
              ].map((x) => (
                <li key={x} className="flex items-start gap-2.5 font-mukta text-sm text-[#FBF6EC]/85">
                  <Check size={16} className="mt-0.5 shrink-0 text-[#C99B55]" strokeWidth={2.5} />{x}
                </li>
              ))}
            </ul>
            <button onClick={() => openWaitlist("Family")} data-testid="families-early-access-btn"              className="mt-6 rounded-full bg-[#FBF6EC] px-6 py-3 font-mukta font-semibold text-[#3B221B] transition-colors hover:bg-[#F3EADA] focus:outline-none focus:ring-2 focus:ring-[#FBF6EC]/60">
              Book now
            </button>
          </div>
          <div>
            <p className="font-mukta text-xs font-semibold uppercase tracking-widest text-[#C99B55]">For Purohiths & Poojaris</p>
            <h3 className="mt-1 font-eczar text-2xl font-semibold text-[#FBF6EC]">Earn with dignity</h3>
            <ul className="mt-4 space-y-2.5">
              {[
                "₹0 to join — no onboarding or listing fee",
                "Steady bookings beyond temple daily collections",
                "You set your availability, we bring the families",
                "Support in Kannada from real people",
              ].map((x) => (
                <li key={x} className="flex items-start gap-2.5 font-mukta text-sm text-[#FBF6EC]/85">
                  <Check size={16} className="mt-0.5 shrink-0 text-[#C99B55]" strokeWidth={2.5} />{x}
                </li>
              ))}
            </ul>
            <button onClick={() => openWaitlist("Purohith")} data-testid="purohith-register-btn"
              className="mt-6 rounded-full border border-[#FBF6EC]/70 px-6 py-3 font-mukta font-semibold text-[#FBF6EC] transition-colors hover:bg-[#FBF6EC]/10 focus:outline-none focus:ring-2 focus:ring-[#FBF6EC]/60">
              Register as a purohith
            </button>
          </div>
        </div>
      </div>
    </Reveal>
    <div className="mt-6 text-center">
      <Link to="/purohiths" data-testid="view-purohiths-page"
        className="inline-flex items-center gap-1.5 font-mukta font-semibold text-[#B23A2B] hover:text-[#9a2f22]">
        More for purohiths & poojaris <ArrowRight size={16} />
      </Link>
    </div>
  </section>
);

/* ---------- 9. how it works ---------- */
const HowItWorks = () => (
  <section className="mx-auto max-w-7xl px-4 pb-16 md:px-8 md:pb-20">
    <SectionHead eyebrow="ಹೇಗೆ ಕೆಲಸ ಮಾಡುತ್ತದೆ" title="How it works" />
    <div className="mt-10 grid gap-6 md:grid-cols-3">
      {HOW_STEPS.map((s, i) => (
        <Reveal key={s.n} delay={i * 0.1}>
          <div className="h-full rounded-[14px] border border-[#A8722A]/25 bg-[#FFFDF8] p-6 shadow-sm" data-testid={`how-step-${s.n}`}>
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#B23A2B] font-eczar text-lg font-bold text-[#FFFDF8]">{s.n}</span>
            <h3 className="mt-4 font-eczar text-lg font-semibold text-[#3B221B]">{s.t}</h3>
            <p className="mt-1.5 font-mukta text-sm text-[#6B5044]">{s.d}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

/* ---------- 10. mission band ---------- */
const MissionBand = () => (
  <section className="border-y border-[#A8722A]/20 bg-[#F3EADA]">
    <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 py-16 md:grid-cols-2 md:px-8 md:py-20">
      <Reveal>
        <blockquote className="font-eczar text-2xl font-medium leading-relaxed text-[#3B221B] md:text-3xl" data-testid="mission-quote">
          "Every booking on this platform creates <span className="text-[#B23A2B]">rural employment</span> for a purohith whose income once depended on a temple's daily collection plate."
        </blockquote>
      </Reveal>
      <Reveal delay={0.12}>
        <p className="font-mukta text-base leading-relaxed text-[#6B5044] md:text-lg">
          Many purohiths in rural Karnataka earn only what the day's visitors leave at the temple. PurohithConnect exists to change that — connecting their knowledge and dedication with families in the city who are searching for exactly that trust. When you book, you are not just arranging a ceremony. You are supporting a livelihood.
        </p>
        <div className="mt-7 flex items-center gap-3">
          <figure className="flex-1">
            <img src={IMAGES.priestBefore} alt="A purohith at a rural Karnataka temple, before PurohithConnect"
              className="h-36 w-full rounded-[14px] object-cover shadow-sm md:h-44" data-testid="mission-photo-before" />
          </figure>
          <ArrowRight className="shrink-0 text-[#A8722A]" size={22} aria-hidden="true" />
          <figure className="flex-1">
            <img src={IMAGES.priestAfter} alt="The same purohith performing a home ceremony for a family in Bengaluru"
              className="h-36 w-full rounded-[14px] object-cover shadow-sm md:h-44" data-testid="mission-photo-after" />
          </figure>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ---------- 11. areas ---------- */
const Areas = () => (
  <section className="mx-auto max-w-4xl px-4 py-14 text-center md:py-16">
    <Reveal>
      <p className="font-mukta text-sm font-semibold uppercase tracking-widest text-[#A8722A]">Serving all of Bengaluru</p>
      <p className="mt-3 font-eczar text-xl text-[#3B221B] md:text-2xl">
        From Malleshwaram to Whitefield, Jayanagar to Yelahanka, Rajajinagar to Electronic City — wherever your home is, your purohith reaches you.
      </p>
    </Reveal>
  </section>
);

/* ---------- 11b. blog teaser ---------- */
const BlogTeaser = () => {
  const posts = BLOG_POSTS.slice(0, 3);
  return (
    <section className="mx-auto max-w-7xl px-4 py-16 md:px-8 md:py-20" data-testid="landing-blog-teaser">
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
        <SectionHead eyebrow="ಬ್ಲಾಗ್" title="Guides from our blog" intro="Tradition-aware notes for families — poojas, muhurtas and samskaras, explained simply." />
      </div>
      <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {posts.map((p, i) => (
          <Reveal key={p.slug} delay={(i % 3) * 0.07}>
            <Link to={`/blog/${p.slug}`} data-testid={`landing-blog-card-${p.slug}`}
              className="group flex h-full flex-col overflow-hidden rounded-[14px] border border-[#A8722A]/25 bg-[#FFFDF8] shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md">
              <div className="h-44 w-full overflow-hidden bg-[#F3EADA]/50">
                <img src={p.img} alt={p.titleEn} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
              </div>
              <div className="flex flex-1 flex-col p-5">
                <span className="self-start rounded-full bg-[#A8722A]/10 px-2.5 py-0.5 font-mukta text-[11px] font-semibold text-[#A8722A]">{p.cat}</span>
                <p className="mt-3 font-baloo text-base text-[#B23A2B]">{p.titleKn}</p>
                <h3 className="mt-0.5 font-eczar text-lg font-semibold leading-snug text-[#3B221B]">{p.titleEn}</h3>
                <p className="mt-2 flex-1 font-mukta text-sm text-[#6B5044] line-clamp-3">{p.excerpt}</p>
                <span className="mt-4 font-mukta text-xs text-[#6B5044]">{formatDate(p.date)} · {p.readMins} min read</span>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
      <div className="mt-8 text-center">
        <Link to="/blog" data-testid="landing-view-all-blogs"
          className="inline-flex items-center gap-1.5 font-mukta font-semibold text-[#B23A2B] hover:text-[#9a2f22]">
          Read all guides <ArrowRight size={16} />
        </Link>
      </div>
    </section>
  );
};

/* ---------- 12. final CTA ---------- */
const FinalCTA = ({ openWaitlist }) => (
  <section id="early-access" className="mx-auto max-w-4xl px-4 pb-20 text-center">
    <Reveal>
      <h2 className="font-eczar text-3xl font-semibold text-[#3B221B] md:text-4xl">Your next occasion deserves the right hands.</h2>
      <p className="mx-auto mt-3 max-w-xl font-mukta text-base text-[#6B5044]">
        Join the early access list — we'll notify you the moment bookings open in Bengaluru.
      </p>
      <div className="mt-7 flex flex-wrap justify-center gap-3">
        <PrimaryBtn onClick={() => openWaitlist("Family")} testId="final-early-access-btn">Book now</PrimaryBtn>
        <OutlineBtn onClick={() => openWaitlist("Purohith")} testId="final-join-priest-btn">Join as a purohith</OutlineBtn>
      </div>
      <p className="mt-8 font-baloo text-lg text-[#A8722A]">|| ಶುಭಮಸ್ತು ||</p>
    </Reveal>
  </section>
);

/* ---------- 13. footer ---------- */
const Footer = () => (
  <footer className="border-t border-[#A8722A]/20 bg-[#F3EADA]">
    {/* Help / contact — for booking issues or any information */}
    <div className="border-b border-[#A8722A]/15">
      <div className="mx-auto max-w-7xl px-4 py-6 md:px-8" data-testid="footer-contact">
        <p className="font-baloo text-base text-[#B23A2B]">ಸಹಾಯ ಬೇಕೇ? · Need help?</p>
        <p className="mt-1 font-mukta text-sm text-[#6B5044]">
          ಬುಕಿಂಗ್ ವೇಳೆ ಸಮಸ್ಯೆ ಅಥವಾ ಯಾವುದೇ ಮಾಹಿತಿಗಾಗಿ ನಮ್ಮನ್ನು ಸಂಪರ್ಕಿಸಿ · For any booking issue or information, reach us:
        </p>
        <div className="mt-3 flex flex-col gap-2.5 sm:flex-row sm:flex-wrap sm:items-center sm:gap-6">
          <a href="mailto:namaste@purohithconnect.com" data-testid="footer-email-namaste"
            className="inline-flex items-center gap-2 font-mukta text-sm text-[#3B221B] hover:text-[#B23A2B]">
            <Mail size={15} className="text-[#A8722A]" /> namaste@purohithconnect.com
          </a>
          <a href="mailto:purohithconnect.admin@purohithconnect.com" data-testid="footer-email-admin"
            className="inline-flex items-center gap-2 font-mukta text-sm text-[#3B221B] hover:text-[#B23A2B]">
            <Mail size={15} className="text-[#A8722A]" /> purohithconnect.admin@purohithconnect.com
          </a>
          <a href="tel:+919110248636" data-testid="footer-phone"
            className="inline-flex items-center gap-2 font-mukta text-sm text-[#3B221B] hover:text-[#B23A2B]">
            <Phone size={15} className="text-[#A8722A]" /> +91 91102 48636
          </a>
          <a href="https://wa.me/919110248636" target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp"
            className="inline-flex items-center gap-2 font-mukta text-sm text-[#128C3E] hover:text-[#0e6b2f]">
            <MessageCircle size={15} /> WhatsApp — chat in one tap
          </a>
        </div>
      </div>
    </div>
    <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 px-4 py-6 font-mukta text-sm text-[#6B5044] sm:flex-row md:px-8">
      <div className="flex flex-col items-center gap-2 sm:items-start">
        <img src="/brand/pc-wordmark.png" alt="PurohithConnect™" className="h-6 w-auto" data-testid="footer-logo" />
        <p data-testid="footer-copyright">© 2026 PurohithConnect™ · Built in Bengaluru, Karnataka — for India</p>
      </div>
      <div className="flex flex-wrap justify-center gap-4">
        <Link to="/occasions" className="hover:text-[#3B221B]">Occasions</Link>
        <Link to="/parayana" className="hover:text-[#3B221B]">Parayana</Link>
        <Link to="/astrology" className="hover:text-[#3B221B]">Astrology</Link>
        <Link to="/patashala" className="hover:text-[#3B221B]" data-testid="footer-patashala-link">For Patashala Students</Link>
        <Link to="/traditions" className="hover:text-[#3B221B]">Traditions</Link>
        <Link to="/purohiths" className="hover:text-[#3B221B]">For Purohiths</Link>
        <Link to="/stotras" className="hover:text-[#3B221B]">Stotras</Link>
        <Link to="/festivals" className="hover:text-[#3B221B]">Festival Calendar</Link>
        <Link to="/blog" className="hover:text-[#3B221B]" data-testid="footer-blog-link">Blogs</Link>
        <Link to="/contact" className="hover:text-[#3B221B]" data-testid="footer-contact-link">Contact</Link>
      </div>
    </div>
  </footer>
);

/* ---------- page ---------- */
export default function Landing() {
  const [kn, setKn] = useState(false);
  const [waitlist, setWaitlist] = useState({ open: false, role: "Family" });
  const openWaitlist = () => openBooking({ location: "landing" });

  return (
    <div className="min-h-screen bg-[#FBF6EC] text-[#3B221B]" style={{ colorScheme: "light" }}>
      <Header kn={kn} setKn={setKn} openWaitlist={openWaitlist} />
      <main>
        <Hero kn={kn} openWaitlist={openWaitlist} />
        <PanchangaWidget kn={kn} />
        <BlessedTraditions kn={kn} />
        <TrustStrip />
        <Catalogue openWaitlist={openWaitlist} />
        <Traditions />
        <BengaluruBand />
        <DualB2C openWaitlist={openWaitlist} />
        <HowItWorks />
        <MissionBand />
        <Areas />
        <BlogTeaser />
        <FinalCTA openWaitlist={openWaitlist} />
      </main>
      <Footer />
      <WaitlistModal key={`${waitlist.open}-${waitlist.role}`} open={waitlist.open} defaultRole={waitlist.role}
        onClose={() => setWaitlist((w) => ({ ...w, open: false }))} />
    </div>
  );
}
